import React from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, BookOpen } from "lucide-react";
import { DATA_SOURCES, BIASES } from "@/data/cardGameData";
import { getCardDisplayTitle } from "@/views/cardgame/cardDisplay";

const copy = {
  nl: {
    step: "Stap 6",
    title: "Reflecteer als team",
    intro: "Je hebt twee verschillende synthetische gebruikers op hetzelfde concept zien reageren. Wat betekent dit voor je ontwerp?",
    roundCards: (round) => `Ronde ${round} kaarten`,
    noCards: "Geen kaarten",
    reflectionLabel: "Reflectie van je team",
    pairedTitle: "Vergelijkingen uit de vorige stap",
    output1: "Output 1",
    output2: "Output 2",
    placeholder: "Wat heb je geleerd? Wat zou je anders doen? Welke aannames zijn uitgedaagd?",
    back: "Terug",
    conclude: "Afronden ->",
  },
  en: {
    step: "Step 6",
    title: "Reflect as a team",
    intro: "You've seen two different synthetic users react to the same concept. What does this mean for your design?",
    roundCards: (round) => `Round ${round} cards`,
    noCards: "No cards",
    reflectionLabel: "Your team's reflection",
    pairedTitle: "Comparisons from the previous step",
    output1: "Output 1",
    output2: "Output 2",
    placeholder: "What did you learn? What would you do differently? What assumptions were challenged?",
    back: "Back",
    conclude: "Conclude ->",
  },
};

function getCategoryOrder(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return 0;
  if (BIASES.find((c) => c.id === id)) return 1;
  return 2;
}

function sortCardsByCategory(cards) {
  return [...cards].sort((a, b) => getCategoryOrder(a.id) - getCategoryOrder(b.id));
}

export function ReflectionStep({
  round1,
  round2,
  reflection,
  setReflection,
  comparisonReflections = [],
  onNext,
  onBack,
  language = "nl",
}) {
  const t = copy[language] || copy.nl;
  const completedComparisons = comparisonReflections.filter((item) => item.round1?.trim() && item.round2?.trim());

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(97,203,163,0.18)] text-[#905cff]">
            <BookOpen className="h-5 w-5" />
          </span>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#905cff] font-medium">{t.step}</div>
            <h2 className="font-display text-xl font-semibold text-black">{t.title}</h2>
          </div>
        </div>

        <p className="text-sm text-black/60 mb-5 leading-relaxed">
          {t.intro}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { label: t.roundCards(1), cards: round1.cards, color: "#5803FD", bg: "rgba(88,3,253,0.04)", border: "rgba(88,3,253,0.15)" },
            { label: t.roundCards(2), cards: round2.cards, color: "#E85D04", bg: "rgba(232,93,4,0.04)", border: "rgba(232,93,4,0.15)" },
          ].map(({ label, cards, color, bg, border }) => (
            <div key={label} className="rounded-xl border p-3" style={{ background: bg, borderColor: border }}>
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color }}>{label}</div>
              <div className="text-xs text-black/60">{sortCardsByCategory(cards).map((c) => getCardDisplayTitle(c, language)).join(", ") || t.noCards}</div>
            </div>
          ))}
        </div>

        {completedComparisons.length > 0 && (
          <div className="rounded-xl border border-[#905cff]/20 bg-[rgba(144,92,255,0.04)] p-4 mb-5">
            <div className="text-xs text-[#905cff] uppercase tracking-wider mb-3">{t.pairedTitle}</div>
            <div className="space-y-3">
              {completedComparisons.map((item) => (
                <div key={item.id} className="rounded-lg border border-black/8 bg-white p-3">
                  <div className="font-medium text-sm text-black mb-2">{item[language === "en" ? "questionEn" : "questionNl"]}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5803FD] mb-1">{t.output1}</div>
                      <p className="text-sm text-black/70 leading-relaxed">{item.round1}</p>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#E85D04] mb-1">{t.output2}</div>
                      <p className="text-sm text-black/70 leading-relaxed">{item.round2}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-black/40 uppercase tracking-wider mb-2">{t.reflectionLabel}</div>
        <Textarea
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder={t.placeholder}
          rows={5}
          className="rounded-xl resize-none"
        />
      </div>

      <div className="flex gap-3">
        <Button onClick={onBack} variant="outline" className="rounded-xl flex-none">
          <ChevronLeft className="h-4 w-4 mr-1" /> {t.back}
        </Button>
        <Button onClick={onNext} className="flex-1 rounded-xl bg-[#905cff] text-white hover:bg-[#5802ff] py-3">
          {t.conclude}
        </Button>
      </div>
    </div>
  );
}
